/**
 * 筛选工作台 - 智能化绿色债券企业筛选审核平台
 * 基于 docs/问卷/交易所初筛问卷设计.md
 * 使用StandardPageLayout确保样式一致性
 */
import React, { useState, useCallback } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Row,
  Col,
  message,
  Table,
  Tag,
  Progress,
  Statistic,
  Empty,
  Slider,
  Select,
  Input,
  DatePicker,
  Modal,
  Form,
  Radio,
  Checkbox,
  Spin,
} from 'antd';
import {
  FilterOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SearchOutlined,
  AuditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { StandardPageLayout } from '../components/layout';
import { ResponsiveScreeningTable, ScreeningStatistics, ResponsiveFilterPanel } from '../components/screening';
import { UnifiedFilters } from '../components/unified';
import { QuestionnaireDetailModal } from '../components/screening/QuestionnaireDetailModal';
import { useResponsive } from '../hooks/useResponsive';
import { EMERALD_THEME } from '../config/designTokens';
import { useGetChannelsQuery } from '../store/api/channelApi';
import { 
  QuestionnaireStatus,
  QuestionnaireFilters,
  InitialScreeningQuestionnaire,
  ScreeningDecision,
} from '../types/questionnaire';
import {
  useGetQuestionnairesQuery,
  useGetQuestionnaireStatisticsQuery,
  useBatchOperationQuestionnairesMutation,
  useReviewQuestionnaireMutation,
  useStartQuestionnaireReviewMutation,
} from '../store/api/questionnaireApi';

const { Text } = Typography;
const { TextArea } = Input;

// 移除ViewMode类型定义，不再需要Tab切换

const ScreeningWorkbench: React.FC = () => {
  const { t } = useTranslation();
  // 响应式状态
  const { isMobile, isTablet, isDesktop } = useResponsive();
  
  // 本地状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState<InitialScreeningQuestionnaire | null>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);
  const [reviewForm] = Form.useForm();
  
  // 筛选条件
  const [filters, setFilters] = useState<QuestionnaireFilters>({
    status: [],
    industry: [],
    assetScale: [],
    creditRating: [],
    riskLevel: [],
    scoreRange: [0, 100],
    dateRange: null,
    search: '',
    channel: [], // 渠道筛选
  });
  
  // 分页参数
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // 构建查询参数，包含筛选条件 - Bug修复: 添加默认排序（按提交时间倒序）
  const queryParams = {
    page: pagination.page,
    limit: pagination.limit,
    // 筛选条件
    status: filters.status,
    industry: filters.industry,
    creditRating: filters.creditRating,
    search: filters.search,
    // Bug修复: 渠道筛选 - 兼容数组和字符串格式
    channelId: Array.isArray(filters.channel)
      ? (filters.channel.length > 0 ? filters.channel[0] : undefined)
      : (filters.channel && filters.channel !== '' ? filters.channel : undefined),
    // Bug修复: scoreRange转为字符串格式确保正确传递
    scoreRange: filters.scoreRange ? `${filters.scoreRange[0]},${filters.scoreRange[1]}` : undefined,
    dateRange: filters.dateRange,
    // Bug修复: 默认按提交时间倒序排序（最新的在最上面）
    sortBy: 'submittedAt',
    sortOrder: 'desc' as const,
  };
  
  console.log('Query params:', queryParams);
  
  const { 
    data: questionnairesData, 
    isLoading: isLoadingQuestionnaires,
    refetch: refetchQuestionnaires,
    error: questionnairesError
  } = useGetQuestionnairesQuery(queryParams);

  const { 
    data: statisticsData, 
    isLoading: isLoadingStatistics 
  } = useGetQuestionnaireStatisticsQuery();

  // 获取渠道列表用于筛选
  const { data: channelsData } = useGetChannelsQuery({ 
    page: 1, 
    limit: 100,  // 获取所有渠道用于筛选
    status: ['active'] // 只获取活跃渠道
  });

  // 批量操作功能已移除（无实际价值）
  // const [batchOperation, { isLoading: isBatchLoading }] = useBatchOperationQuestionnairesMutation();
  const [reviewQuestionnaire, { isLoading: isReviewLoading }] = useReviewQuestionnaireMutation();
  const [startQuestionnaireReview] = useStartQuestionnaireReviewMutation();

  // 处理筛选条件变化
  const handleFiltersChange = useCallback((key: string, value: any) => {
    console.log('筛选条件变化:', key, value);
    setFilters(prev => {
      // Bug修复: channel是单选select，返回字符串，需要转为数组
      let processedValue = value;
      if (key === 'channel') {
        // select返回字符串或undefined，转为数组格式
        processedValue = value && value !== '' ? [value] : [];
      }
      const newFilters = { ...prev, [key]: processedValue };
      setPagination(prev => ({ ...prev, page: 1 })); // 重置到第一页
      return newFilters;
    });
  }, []);

  // 清除筛选条件
  const handleClearFilters = useCallback(() => {
    setFilters({
      status: [],
      industry: [],
      assetScale: [],
      creditRating: [],
      riskLevel: [],
      scoreRange: [0, 100],
      dateRange: null,
      search: '',
      channel: [], // Bug修复: 清除时重置渠道筛选
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // 处理分页变化
  const handlePaginationChange = useCallback((page: number, pageSize?: number) => {
    setPagination(prev => ({
      page: pageSize && pageSize !== prev.limit ? 1 : page, // 改变pageSize时重置为第1页
      limit: pageSize || prev.limit,
    }));
  }, []);

  // 处理行选择变化
  const handleSelectionChange = useCallback((selectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(selectedRowKeys);
  }, []);

  // 查看问卷详情
  const handleViewDetail = useCallback((questionnaire: InitialScreeningQuestionnaire) => {
    setCurrentQuestionnaire(questionnaire);
    setIsDetailModalVisible(true);
  }, []);

  // 批量操作功能已移除（无实际价值）

  // 单个问卷决策
  const handleMakeDecision = useCallback((questionnaire: InitialScreeningQuestionnaire, decision: ScreeningDecision) => {
    setCurrentQuestionnaire(questionnaire);
    setIsReviewModalVisible(true);
    reviewForm.setFieldsValue({
      decision: decision === ScreeningDecision.AUTO_PASS ? 'approved' : 'rejected',
      comments: '',
      reasons: [],
    });
  }, [reviewForm]);

  // 详情模态框中的审核处理
  const handleDetailReview = useCallback(async (
    questionnaireId: string, 
    decision: 'approved' | 'rejected', 
    comments?: string, 
    reasons?: string[]
  ) => {
    try {
      const result = await reviewQuestionnaire({
        questionnaireId: questionnaireId,
        decision,
        comments: comments || '',
        reasons: reasons || [],
      }).unwrap();
      
      message.success(t('screening:message.reviewSuccess', { result: decision === 'approved' ? t('screening:message.approved') : t('screening:message.rejected') }));
      refetchQuestionnaires();
    } catch (error: any) {
      message.error(error.data?.error?.message || t('screening:message.reviewFailed'));
      throw error; // 重新抛出错误，让模态框处理
    }
  }, [reviewQuestionnaire, refetchQuestionnaires]);

  // 提交审核决策
  const handleSubmitReview = useCallback(async () => {
    if (!currentQuestionnaire) return;

    try {
      const values = await reviewForm.validateFields();
      
      await reviewQuestionnaire({
        questionnaireId: currentQuestionnaire._id!,
        decision: values.decision === 'approved' ? 'approved' : 'rejected',
        comments: values.comments,
        reasons: values.reasons,
      }).unwrap();

      message.success(t('screening:message.submitSuccess'));
      setIsReviewModalVisible(false);
      setCurrentQuestionnaire(null);
      reviewForm.resetFields();
      refetchQuestionnaires();
    } catch (error: any) {
      message.error(error.data?.error?.message || t('screening:message.submitFailed'));
    }
  }, [currentQuestionnaire, reviewForm, reviewQuestionnaire, refetchQuestionnaires]);

  // 开始审核处理 - 将问卷状态从 submitted 变为 under_review
  const handleStartReview = useCallback(async (questionnaireId: string) => {
    try {
      await startQuestionnaireReview(questionnaireId).unwrap();
      refetchQuestionnaires();
      // 关闭详情弹窗，让用户重新打开以看到更新后的状态
      setIsDetailModalVisible(false);
      setCurrentQuestionnaire(null);
    } catch (error: any) {
      throw error; // 让模态框处理错误
    }
  }, [startQuestionnaireReview, refetchQuestionnaires]);

  // 移除Tab配置，直接显示问卷列表内容

  return (
    <StandardPageLayout
      title={t('screening:title')}
      subtitle={t('screening:subtitle')}
      breadcrumbs={[
        { label: t('screening:breadcrumb.home'), path: '/admin' },
        { label: t('screening:breadcrumb.screening') },
      ]}
      statistics={[
        {
          value: questionnairesData?.data?.total ?? statisticsData?.data?.total_received ?? 0,
          label: t('screening:stats.total'),
          color: EMERALD_THEME.colors.statistics.total,
        },
        {
          value: statisticsData?.data?.pending_count || 0,
          label: t('screening:stats.pending'),
          color: EMERALD_THEME.colors.statistics.pending,
        },
        {
          value: statisticsData?.data?.under_review_count || 0,
          label: t('screening:stats.reviewing'),
          color: '#fa8c16',
        },
        {
          value: statisticsData?.data?.approved_count || 0,
          label: t('screening:stats.approved'),
          color: EMERALD_THEME.colors.statistics.completed,
        },
        {
          value: statisticsData?.data?.rejected_count || 0,
          label: t('screening:stats.rejected'),
          color: EMERALD_THEME.colors.statistics.rejected,
        },
      ]}
      icon={<AuditOutlined style={{ fontSize: '24px', color: 'white' }} />}
      tags={[t('screening:tags.smartFilter'), t('screening:tags.dataAnalysis'), t('screening:tags.efficientReview')]}
    >
      {/* 使用响应式筛选面板 */}
      <ResponsiveFilterPanel
        filters={filters}
        filterConfigs={[
          {
            key: 'search',
            type: 'search',
            placeholder: t('screening:filter.searchPlaceholder'),
            span: isMobile ? 24 : 8,
          },
          {
            key: 'status',
            type: 'multiSelect',
            placeholder: t('screening:filter.statusPlaceholder'),
            span: isMobile ? 24 : 4,
            options: [
              { label: t('screening:status.submitted'), value: 'submitted' },
              { label: t('screening:status.under_review'), value: 'under_review' },
              { label: t('screening:status.approved'), value: 'approved' },
              { label: t('screening:status.rejected'), value: 'rejected' },
              { label: t('screening:status.need_supplement'), value: 'need_supplement' },
            ],
          },
          {
            key: 'industry',
            type: 'select',
            placeholder: t('screening:filter.industryPlaceholder'),
            span: isMobile ? 24 : 4,
            options: [
              { label: t('screening:industry.manufacturing'), value: 'manufacturing' },
              { label: t('screening:industry.power'), value: 'power' },
              { label: t('screening:industry.other'), value: 'other' },
            ],
          },
          {
            key: 'scoreRange',
            type: 'custom',
            span: isMobile ? 24 : 4,
            customRender: (value: any, onChange: any) => (
              <div>
                <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                  {t('screening:filter.aiScoreLabel')}: {value?.[0] || 0} - {value?.[1] || 100}
                </div>
                <Slider
                  range
                  min={0}
                  max={100}
                  value={value || [0, 100]}
                  onChange={onChange}
                  style={{ width: '100%' }}
                />
              </div>
            ),
          },
          {
            key: 'channel',
            type: 'select',
            placeholder: t('screening:filter.channelPlaceholder'),
            span: isMobile ? 24 : 4,
            options: [
              { label: t('screening:filter.allChannels'), value: '' },
              ...(channelsData?.data.channels?.map(channel => ({
                label: channel.name,
                value: channel.code || channel._id || '',
              })) || []),
            ],
          },
        ]}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        channelsData={channelsData}
      />

      <div style={{
        background: 'white',
        borderRadius: EMERALD_THEME.borderRadius.lg,
        padding: isMobile ? EMERALD_THEME.spacing.md : EMERALD_THEME.spacing.lg,
        boxShadow: EMERALD_THEME.shadows.sm,
        marginTop: 0,
      }}>

        {/* 问卷表格 - 响应式优化版 */}
        <ResponsiveScreeningTable
          questionnaires={questionnairesData?.data?.questionnaires || []}
          selectedQuestionnaires={selectedRowKeys as string[]}
          loading={isLoadingQuestionnaires}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: questionnairesData?.data?.total || 0,
            onChange: handlePaginationChange,
          }}
          onSelectionChange={handleSelectionChange}
          onViewDetails={(questionnaire: any) => {
            setCurrentQuestionnaire(questionnaire);
            setIsDetailModalVisible(true);
          }}
          onReview={(questionnaire: any) => {
            setCurrentQuestionnaire(questionnaire);
            setIsReviewModalVisible(true);
          }}
        />
      </div>


      {/* 审核决策弹窗 */}
      <Modal
        title={t('screening:review.title')}
        open={isReviewModalVisible}
        onCancel={() => setIsReviewModalVisible(false)}
        onOk={handleSubmitReview}
        confirmLoading={isReviewLoading}
        width={600}
        centered
      >
        <Form form={reviewForm} layout="vertical">
          <Form.Item
            name="decision"
            label={t('screening:review.decision')}
            rules={[{ required: true, message: t('screening:review.decisionRequired') }]}
          >
            <Radio.Group>
              <Radio value="approved">{t('screening:review.approve')}</Radio>
              <Radio value="rejected">{t('screening:review.reject')}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues: any, currentValues: any) =>
              prevValues.decision !== currentValues.decision
            }
          >
            {({ getFieldValue }: any) =>
              getFieldValue('decision') === 'rejected' && (
                <Form.Item
                  name="reasons"
                  label={t('screening:review.rejectionReasons')}
                  rules={[{ required: true, message: t('screening:review.rejectionRequired') }]}
                >
                  <Checkbox.Group>
                    <Space direction="vertical">
                      <Checkbox value={t('screening:rejectionReasons.incompleteInfo')}>{t('screening:rejectionReasons.incompleteInfo')}</Checkbox>
                      <Checkbox value={t('screening:rejectionReasons.notMeetStandard')}>{t('screening:rejectionReasons.notMeetStandard')}</Checkbox>
                      <Checkbox value={t('screening:rejectionReasons.financialIssue')}>{t('screening:rejectionReasons.financialIssue')}</Checkbox>
                      <Checkbox value={t('screening:rejectionReasons.environmentalIssue')}>{t('screening:rejectionReasons.environmentalIssue')}</Checkbox>
                      <Checkbox value={t('screening:rejectionReasons.legalRisk')}>{t('screening:rejectionReasons.legalRisk')}</Checkbox>
                      <Checkbox value={t('screening:rejectionReasons.other')}>{t('screening:rejectionReasons.other')}</Checkbox>
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="comments"
            label={t('screening:review.commentsOptional')}
          >
            <TextArea
              rows={4}
              placeholder={t('screening:review.commentsPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 问卷详情模态框 */}
      <QuestionnaireDetailModal
        visible={isDetailModalVisible}
        questionnaire={currentQuestionnaire}
        onClose={() => {
          setIsDetailModalVisible(false);
          setCurrentQuestionnaire(null);
        }}
        onReview={handleDetailReview}
        onStartReview={handleStartReview}
      />
    </StandardPageLayout>
  );
};

export default ScreeningWorkbench;